/**
 * MasterDataTlcBankFormDialog — Create/Edit `tlc_bank` (Access frmMdQryTLCBank).
 * Compact Sales Contract style dialog. Single-column.
 *
 * `account_no` là PK text: required, disable khi edit.
 * `bank_code` chọn từ combo (Autocomplete) — options là danh sách bank code (`bank`).
 */
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdTlcBankMutation,
  useListMdBanksQuery,
  useUpdateMdTlcBankMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataTlcBank } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx, dialogTitleTextSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataTlcBank | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  account_no: string
  bank_code: string
  branch: string
  swift_code: string
  others: string
}

const EMPTY: FormState = {
  account_no: '',
  bank_code: '',
  branch: '',
  swift_code: '',
  others: '',
}

const ALL_ROWS_PAGE_SIZE = 1_000_000

export default function MasterDataTlcBankFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createTlcBank, { isLoading: creating }] = useCreateMdTlcBankMutation()
  const [updateTlcBank, { isLoading: updating }] = useUpdateMdTlcBankMutation()
  const loading = creating || updating

  const { data: banksData } = useListMdBanksQuery({ page: 1, page_size: ALL_ROWS_PAGE_SIZE })
  const bankOptions = useMemo(
    () => (banksData?.items ?? []).map((b) => b.bank).filter((v): v is string => !!v),
    [banksData],
  )

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    if (isEdit && initialValue) {
      setForm({
        account_no: initialValue.account_no ?? '',
        bank_code: initialValue.bank_code ?? '',
        branch: initialValue.branch ?? '',
        swift_code: initialValue.swift_code ?? '',
        others: initialValue.others ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const codeEmpty = !form.account_no.trim()
  const canSave = !codeEmpty && !loading

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const buildBody = () => ({
    bank_code: form.bank_code.trim() || null,
    branch: form.branch.trim() || null,
    swift_code: form.swift_code.trim() || null,
    others: form.others.trim() || null,
  })

  const handleSave = async () => {
    if (codeEmpty) {
      setShowErrors(true)
      return
    }
    try {
      if (isEdit && initialValue) {
        await updateTlcBank({ code: initialValue.account_no, body: buildBody() }).unwrap()
        snack.success('TLC bank updated')
      } else {
        await createTlcBank({ account_no: form.account_no.trim(), ...buildBody() }).unwrap()
        snack.success('TLC bank created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit TLC Bank' : 'New TLC Bank'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.account_no}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label="Account No"
              size="small"
              fullWidth
              required
              disabled={isEdit}
              error={showErrors && codeEmpty}
              value={form.account_no}
              onChange={(e) => set('account_no', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: isEdit ? -1 : 1 }}
              helperText={isEdit ? 'Account No cannot be changed after creation.' : undefined}
              sx={isEdit ? roSx : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              size="small"
              fullWidth
              options={bankOptions}
              value={form.bank_code || null}
              onChange={(_e, v) => set('bank_code', v ?? '')}
              isOptionEqualToValue={(o, v) => o === v}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Bank Code"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Branch"
              size="small"
              fullWidth
              value={form.branch}
              onChange={(e) => set('branch', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Swift Code"
              size="small"
              fullWidth
              value={form.swift_code}
              onChange={(e) => set('swift_code', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 4 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Others"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={form.others}
              onChange={(e) => set('others', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 5 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
